import { useMemo } from 'react';

import { Box, Flex, SimpleGrid } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Checkbox, DatePicker, InputLabel, Select, Text, TextInput, TimePicker } from 'components';

import { convertToTimeString } from 'components/time';
import { CreditOptionsResponse, CreditRequestForm } from 'types';
import { AppUtils } from 'utils';

interface CreditRequestPersonalInfoProps {
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

const CreditRequestPersonalInfo = ({
  form,
  options,
  visibleFields,
  readOnly = false,
}: CreditRequestPersonalInfoProps) => {
  const { t } = useTranslation();

  const nationalities = useMemo(
    () => AppUtils.getOrderedCountryList(options['nationality'], t),
    [options, t],
  );

  return (
    <Box>
      <Box p={'md'}>
        <SimpleGrid cols={4}>
          <TextInput
            label={t('user.firstName.label')}
            key={form.key('user.firstName')}
            {...form.getInputProps('user.firstName')}
            disabled={true}
            required={true}
          />
          <TextInput
            label={t('user.lastName.label')}
            key={form.key('user.lastName')}
            {...form.getInputProps('user.lastName')}
            disabled={true}
            required={true}
          />
          <Select
            label={t('user.gender.label')}
            data={['MALE', 'FEMALE', 'OTHER'].map((el) => ({
              value: el,
              label: t(`user.gender.options.${el}`),
            }))}
            key={form.key('gender')}
            {...form.getInputProps('gender')}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          <DatePicker
            label={t('user.birthDate.label')}
            placeholder={t('user.birthDate.placeholder')}
            key={form.key('birthDate')}
            {...form.getInputProps('birthDate')}
            minDate={moment().subtract(65, 'years')}
            maxDate={moment().subtract(18, 'years')}
            required={true}
            disabled={readOnly}
          />
        </SimpleGrid>
        <SimpleGrid cols={4} mt={'sm'}>
          <TextInput
            label={t('user.phone.label')}
            key={form.key('user.phone')}
            {...form.getInputProps('user.phone')}
            disabled={true}
            required={true}
          />
          <TextInput
            label={t('user.email.label')}
            key={form.key('user.email')}
            {...form.getInputProps('user.email')}
            disabled={true}
            required={true}
          />
          <Box>
            <InputLabel label={t('credit.workdayAvailability.label')} />
            <Flex align={'center'} justify={'space-evenly'}>
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                {t('credit.workdayAvailability.from')}
              </Text>
              <TimePicker
                key={form.key('workdayAvailabilityFrom')}
                {...form.getInputProps('workdayAvailabilityFrom')}
                min={options['workdayAvailability']?.min}
                max={options['workdayAvailability']?.max}
                w={'30%'}
                disabled={readOnly}
              />
              <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                {t('credit.workdayAvailability.to')}
              </Text>
              <TimePicker
                key={form.key('workdayAvailabilityTo')}
                {...form.getInputProps('workdayAvailabilityTo')}
                min={options['workdayAvailability']?.min}
                max={options['workdayAvailability']?.max}
                w={'30%'}
                disabled={readOnly}
              />
            </Flex>
            <Checkbox
              key={form.key('saturdayAvailability')}
              label={t('credit.saturdayAvailability.label')}
              {...form.getInputProps('saturdayAvailability', { type: 'checkbox' })}
              customOnClick={() => {
                form.setFieldValue(
                  'saturdayAvailabilityFrom',
                  convertToTimeString(options['saturdayAvailability']?.min || 0),
                );
                form.setFieldValue(
                  'saturdayAvailabilityTo',
                  convertToTimeString(options['saturdayAvailability']?.max || 0),
                );
              }}
              disabled={readOnly}
            />
          </Box>

          {visibleFields.includes('saturdayAvailabilityFrom') &&
            visibleFields.includes('saturdayAvailabilityTo') && (
              <Box>
                <InputLabel label={t('credit.saturdayAvailability.label')} />
                <Flex align={'center'} justify={'space-evenly'}>
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.saturdayAvailability.from')}
                  </Text>
                  <TimePicker
                    key={form.key('saturdayAvailabilityFrom')}
                    {...form.getInputProps('saturdayAvailabilityFrom')}
                    min={options['saturdayAvailability']?.min}
                    max={options['saturdayAvailability']?.max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                  <Text size={'sm'} c={'var(--mantine-color-gray-6)'}>
                    {t('credit.saturdayAvailability.to')}
                  </Text>
                  <TimePicker
                    key={form.key('saturdayAvailabilityTo')}
                    {...form.getInputProps('saturdayAvailabilityTo')}
                    min={options['saturdayAvailability']?.min}
                    max={options['saturdayAvailability']?.max}
                    w={'30%'}
                    disabled={readOnly}
                  />
                </Flex>
              </Box>
            )}
        </SimpleGrid>
      </Box>

      <Box p={'md'} mt={'sm'}>
        <SimpleGrid cols={4}>
          <Select
            label={t('user.nationality.label')}
            data={nationalities}
            key={form.key('nationality')}
            {...form.getInputProps('nationality')}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes('visaType') && (
            <Select
              label={t('user.visa.label')}
              data={options['visaType'].map((el) => ({
                value: el,
                label: t(`user.visa.options.${el}`),
              }))}
              key={form.key('visaType')}
              {...form.getInputProps('visaType')}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes('visaFrom') && (
            <DatePicker
              label={t('user.visa.from')}
              key={form.key('visaFrom')}
              {...form.getInputProps('visaFrom')}
              maxDate={new Date()}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default CreditRequestPersonalInfo;
